import { useCallback, useEffect, useState } from "react";
import {
  useGetHeadroomFinalResultsLazyQuery,
  SegmentationMethod,
  DriverMode,
  HeadroomFinalResponse,
  GetHeadroomFinalResultsQueryVariables,
  HeadroomTotals,
} from "graphql/types/graphql";
import { useHeadroomContext } from "../context/HeadRoomProvider";
import { ApolloError } from "@apollo/client";
import { MAX_ROWS } from "../components/tables/TablePaginationWrapper";

interface UseHeadroomFinalDataResult {
  data: HeadroomFinalResponse[] | undefined;
  totals: HeadroomTotals;
  totalCount: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  loading: boolean;
  error: ApolloError | undefined;
  refetch?: (
    variables?: Partial<GetHeadroomFinalResultsQueryVariables>
  ) => Promise<HeadroomFinalResponse[] | undefined>;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  page: number;
  rowsPerPage: number;
}

export const useHeadroomFinalData = (increasedPenetration: number): UseHeadroomFinalDataResult => {
  const {
    selectedProject,
    tableName,
    segmentGroups,
    customerColumn,
    driver1Type,
    driver1NumeratorType,
    driver1Mode,
    driver2Type,
    driver2NumeratorType,
    driver2Mode,
    headroomDriver,
  } = useHeadroomContext();

  const [cachedData, setCachedData] = useState<HeadroomFinalResponse[] | undefined>(undefined);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [hasPreviousPage, setHasPreviousPage] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(MAX_ROWS);

  const offset = (page - 1) * rowsPerPage;

  const driverType = headroomDriver === "driver1" ? driver1Type : driver2Type;
  const driverModeSelected = headroomDriver === "driver1" ? driver1Mode : driver2Mode;
  const driverNumeratorType = headroomDriver === "driver1" ? driver1NumeratorType : driver2NumeratorType;

  const hasAllInputs =
    selectedProject &&
    tableName &&
    segmentGroups.length > 0 &&
    segmentGroups.every((group) => group.type) &&
    customerColumn &&
    headroomDriver &&
    driverType &&
    driverModeSelected &&
    (driverModeSelected !== "PERCENTAGE" || driverNumeratorType);

  const formattedSegmentGroups = segmentGroups.map((group) => ({
    type: group.type,
    method: group.method as SegmentationMethod,
    segments: group.segments.map((segment) => ({
      id: segment.id,
      minValue: segment.minValue,
      maxValue: segment.maxValue,
    })),
    rangeColumn: group.method === "MANUAL" ? group.type : undefined,
  }));

  const [getHeadroomFinalResults, { data, loading, error }] = useGetHeadroomFinalResultsLazyQuery({
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!loading && !error && data?.getHeadroomFinalResults) {
      setCachedData(data.getHeadroomFinalResults.edges);
      setTotalCount(data.getHeadroomFinalResults.totalCount);
      setHasNextPage(data.getHeadroomFinalResults.hasNextPage);
      setHasPreviousPage(data.getHeadroomFinalResults.hasPreviousPage);
    }
  }, [loading, error, data, page, rowsPerPage, offset]);

  const customRefetch = useCallback(
    async (variables?: Partial<GetHeadroomFinalResultsQueryVariables>) => {
      if (!hasAllInputs) {
        return undefined;
      }
      const result = await getHeadroomFinalResults({
        variables: {
          projectName: selectedProject,
          tableName,
          segmentGroups: formattedSegmentGroups,
          customerColumn,
          driverNumeratorColumn: driverModeSelected === "PERCENTAGE" ? driverNumeratorType : null,
          driverDenominatorColumn: driverType,
          driverMode: driverModeSelected as DriverMode,
          increasedPenetration,
          limit: variables?.limit ?? rowsPerPage,
          offset: variables?.offset ?? offset,
          ...variables,
        },
      });

      return result.data?.getHeadroomFinalResults.edges;
    },
    [
      hasAllInputs,
      getHeadroomFinalResults,
      selectedProject,
      tableName,
      formattedSegmentGroups,
      customerColumn,
      driverModeSelected,
      driverNumeratorType,
      driverType,
      increasedPenetration,
      rowsPerPage,
      offset,
    ]
  );

  const handleSetPage = useCallback(
    (newPage: number) => {
      const maxPage = Math.max(1, Math.ceil(totalCount / rowsPerPage));
      const boundedPage = Math.min(Math.max(1, newPage), maxPage);
      setPage(boundedPage);
    },
    [totalCount, rowsPerPage]
  );

  const handleSetRowsPerPage = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  return {
    data: data?.getHeadroomFinalResults.edges ?? cachedData,
    totals: data?.getHeadroomFinalResults.totals,
    totalCount,
    hasNextPage,
    hasPreviousPage,
    loading,
    error,
    refetch: customRefetch,
    setPage: handleSetPage,
    setRowsPerPage: handleSetRowsPerPage,
    page,
    rowsPerPage,
  };
};
